// @flow
import * as React from 'react'
import styled from 'styled-components'
import { useSearchParams } from 'next/navigation'
import FullBleed from '@components/FullBleed'
import SubscribeFlow1 from '@components/subscribe/SubscribeFlow1'
import SubscribeFlow2 from '@components/subscribe/SubscribeFlow2'
import HeaderLayout1 from './HeaderLayout1'
import HeaderLayout2 from './HeaderLayout2'
import { useAddQueryParam } from '@components/shared/hooks/queryParams'

export type HeaderSectionPropsType = {
  title: string,
  headerType?: '1' | '2',
  description: string | null,
  description2: string | null,
  ctaText: string | null,
  tags?: any | null
}

/**
 * HeaderSection Component
 * This component renders our top level header section for any playable content
 * It looks at header params to determine which subscribe flow or header to render
 * h=1 or h=2 for header changes
 * f=1 or f=2 for subscribe flow changes
 * @param props
 * description: desktop description
 * descrption2: mobile description
 * header: header text
 * @returns {JSX.Element}
 * @constructor
 */
function HeaderSection (props: HeaderSectionPropsType): React.Node {
  const [isSubscribeFlow, setIsSubscribeFlow] = React.useState<boolean>(false)
  const ref: null | React$ElementRef<any> = React.useRef(null)
  const [height, setHeight] = React.useState(null)
  const searchParams = useSearchParams()
  const headerTypes = ['1', '2']
  const [headerType, setHeadType] = React.useState<'1' | '2'>(props.headerType || '1')
  const subscribeFlowTypes = ['1', '2']
  const [subscribeFlowType, setSubscribeFlowType] = React.useState<'1' | '2'>('2')
  const addQueryParams = useAddQueryParam()

  React.useEffect((): void => {
    const h = searchParams?.get('h')
    const f = searchParams?.get('f')
    if (!!h && h !== headerType && headerTypes.includes(h)) {
      setHeadType(h)
    }
    if (!!f && f !== subscribeFlowType && subscribeFlowTypes.includes(f)) {
      setSubscribeFlowType(f)
    }
  }, [searchParams, props.headerType])

  React.useEffect((): (() => void) | void => {
    if (!ref?.current) return

    // Check if ResizeObserver is available (browser compatibility)
    if (typeof window !== 'undefined' && window.ResizeObserver) {
      const resizeObserver = new ResizeObserver((): void => {
        if (ref && ref.current && ref.current.clientHeight) {
          setHeight(ref.current.clientHeight)
        }
      })
      if (ref && ref.current) resizeObserver.observe(ref.current)
      return (): void => resizeObserver.disconnect()
    } else {
      // Fallback for browsers without ResizeObserver
      console.warn('ResizeObserver not available, using fallback')
      const updateHeight = (): void => {
        if (ref && ref.current && ref.current.clientHeight) {
          setHeight(ref.current.clientHeight)
        }
      }
      updateHeight() // Initial height
      window.addEventListener('resize', updateHeight)
      return (): void => window.removeEventListener('resize', updateHeight)
    }
  }, [])

  const handleOpenSubscribe = (): void => {
    addQueryParams('subscribe', 't')
    addQueryParams('n', 's')
    setIsSubscribeFlow(true)
  }

  if (isSubscribeFlow) {
    return (
      <FullBleed>
        <Section height={height}>
          {subscribeFlowType === '2'
            ? <SubscribeFlow2 isInLine />
            : <SubscribeFlow1 isInLine />
          }
        </Section>
      </FullBleed>
    )
  }

  return (
    <FullBleed>
      <Section ref={ref}>
        {headerType === '2'
          ? <HeaderLayout2 {...props} openSubscribe={handleOpenSubscribe} />
          : <HeaderLayout1 {...props} openSubscribe={handleOpenSubscribe} />
        }
      </Section>
    </FullBleed>
  )
}

export default HeaderSection

const Section = styled.section`
 ${(props: any): ?string => {
    if (props.height) return `min-height: ${props.height}px; display: flex;`
 }}
`
